import { ChangeDetectionStrategy, Component, Input } from '@angular/core'
import { FunnelHeaderPromptStatus } from '../../../shared/funnel-checkout-header/funnel-checkout-header.component'
import { distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators'
import { Observable } from 'rxjs'
import { BatmanDataService, CarouselBackgroundStyleGen, ResponsiveCarouselInstance } from '../../batman-data.service'
import { CUSTOMER_SERVICE_URL } from 'src/app/account-settings/customer-service'
import { SiteSettings } from 'src/app/shared/models/site-settings.model'
import { LiveEventStatus } from '../../../../events/events.enum'
import { notNullOrUndefined, isContainerPartial, isEventCardModel } from 'src/app/shared/functions/type-guards'
import { PartialsDataService } from 'src/app/singleton-services/partials/partials-data.service'
import { EventCardModel } from '@flocasts/experience-service-types'
import { PlatformService } from 'src/app/singleton-services/platform.service'
import { GeoIPResponseItem } from 'src/app/shared/geo-ip/geo-ip.service'
import { GeoBlockingService } from 'src/app/singleton-services/geo-blocking.service'
import { LiveEventHeader } from 'src/app/funnel/signup/signup.interface'

/**
 * @deprecated We now have a new landing page so this interface is now deprecated
 */
export interface ILandingPageComponent {
  siteSettings: SiteSettings
  showLoginPrompt: FunnelHeaderPromptStatus
  liveEventHeader: LiveEventHeader | undefined
  isLiveFunnel: boolean
  liveEventRedirect: { status: LiveEventStatus; eventId: number } | undefined
}

/**
 * @deprecated We now have a new landing page so this component is now deprecated
 * @see LandingPageComponent
 */
@Component({
  selector: 'flo-landing-page-legacy',
  templateUrl: './landing-page-legacy.component.html',
  styleUrls: ['./landing-page-legacy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LandingPageLegacyComponent {
  @Input() public landingPageProps: ILandingPageComponent
  @Input() public readonly carouselBackgroundGen: CarouselBackgroundStyleGen
  @Input() public readonly carouselData: readonly ResponsiveCarouselInstance[]

  public CUSTOMER_SERVICE_URL = CUSTOMER_SERVICE_URL

  private _partialRender = {
    hero: true,
    calendar: true,
    carousel: true,
    devices: true,
    faq: true
  }

  @Input()
  set partialRender(config: any) {
    this._partialRender = config
  }

  get partialRender() {
    return this._partialRender
  }

  public showCarousel = false

  public analyticsProps = {
    subpage_category: 'Page Speed Experiment'
  }

  public readonly headline$: Observable<{
    isLiveFunnel: boolean
    text: string
  }> = this.batmanDataService.getCalendarHeadline$.pipe(
    map(calendarHeadline => {
      return {
        text: this.landingPageProps.isLiveFunnel ? calendarHeadline : '',
        isLiveFunnel: this.landingPageProps.isLiveFunnel
      }
    })
  )

  public upNextContent$ = this.partialsDataService.getLiveAndUpcomingEventsPartial().pipe(
    filter(notNullOrUndefined),
    filter(isContainerPartial),
    map(res => res?.data),
    distinctUntilChanged(),
    shareReplay(1)
  )

  /**
   * gets the list live ids from geoblocked events
   */
  public geoBlockedIds$: Observable<number[]> = this.upNextContent$.pipe(
    map(container => container.items as EventCardModel[]),
    map(events => {
      const geoBlockedEvents = events.filter(
        event =>
          isEventCardModel(event) &&
          event.liveEventMetadata?.checkGeoRestriction &&
          event.liveEventMetadata?.liveEventId
      )
      const geoBlockedIds = geoBlockedEvents.map(event => event.liveEventMetadata?.liveEventId)
      const filterOutUndefined = geoBlockedIds.filter(notNullOrUndefined)
      return filterOutUndefined
    }),
    filter(_ => this.platformService.isBrowser),
    switchMap(this.geoBlockingService.getGeoBlockedLiveEvents),
    map((geoIpResponseItem: GeoIPResponseItem[]) => geoIpResponseItem.map(res => res.id)),
    shareReplay(1)
  )

  public constructor(
    private readonly batmanDataService: BatmanDataService,
    private readonly partialsDataService: PartialsDataService,
    private readonly platformService: PlatformService,
    private readonly geoBlockingService: GeoBlockingService
  ) {}
}
